import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import * as commonTypes from '@store/common/types';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { EXISTING_FILTER_PANEL_KEY } from '@components/filter-panel/constants';
import update from 'immutability-helper';
import moment from 'moment';
import * as types from './types';
import { INIT_FILTER, INIT_RULE } from './constants';

export const initialState = {
  type: 'create',
  visible: false,
  activeTabKey: EXISTING_FILTER_PANEL_KEY,
  newFilterPanel: INIT_FILTER[CURRENT_PRODUCT_MODE],
  savedFilterPanel: {
    selectedFilters: [],
  },
  filters: [],
  categories: {},
  dimensions: [],
  defaultFilters: [],
  dataCachedAt: null, // timestamp for caching response
};

const reducerMap = Object.create(null);

reducerMap[types.ADD_AND_RULE] = (state) =>
  update(state, {
    newFilterPanel: {
      rule: { $push: [[{ ...INIT_RULE[CURRENT_PRODUCT_MODE] }]] },
    },
  });

reducerMap[types.ADD_OR_RULE] = (state, action) => {
  const { index } = action;
  const [idx1] = index.split('-');
  return update(state, {
    newFilterPanel: {
      rule: {
        [idx1]: {
          $push: [{ ...INIT_RULE[CURRENT_PRODUCT_MODE] }],
        },
      },
    },
  });
};

reducerMap[types.UPDATE_OR_RULE] = (state, action) => {
  const { index, payload } = action;
  const rule = cloneDeep(state.newFilterPanel.rule);
  const [idx1, idx2] = index.split('-');
  Object.assign(rule[idx1][idx2], payload);
  return update(state, {
    newFilterPanel: {
      rule: {
        [idx1]: {
          [idx2]: {
            $merge: payload,
          },
        },
      },
    },
  });
};

reducerMap[types.START_EDIT_FILTER] = (state, action) => {
  const defaultFilterInfo = initialState.newFilterPanel;
  const filterInfo = pick(action.payload, Object.keys(defaultFilterInfo));
  filterInfo.hidden = action.payload.hidden;
  return update(state, {
    type: {
      $set: 'edit',
    },
    newFilterPanel: {
      $set: {
        ...defaultFilterInfo,
        ...filterInfo,
      },
    },
  });
};

reducerMap[types.STOP_EDIT_FILTER] = (state) => {
  return update(state, {
    type: {
      $set: 'create',
    },
  });
};

reducerMap[types.START_SAVING_CHANGES] = (state) =>
  update(state, {
    newFilterPanel: {
      saving: { $set: true },
    },
  });

reducerMap[types.UPDATE_CONTENT_FILTER] = (state, action) =>
  update(state, {
    newFilterPanel: {
      isContent: { $set: action.isContent },
    },
  });

reducerMap[types.UPDATE_FILTER_HIDDEN] = (state, action) =>
  update(state, {
    newFilterPanel: {
      hidden: { $set: action.hidden },
    },
  });

reducerMap[types.DONE_SAVING_CHANGES] = (state) =>
  update(state, {
    newFilterPanel: {
      saving: {
        $set: false,
      },
    },
  });

reducerMap[types.DELETE_OR_RULE] = (state, action) => {
  const { index } = action;
  const [idx1, idx2] = index.split('-');
  const newState = update(state, {
    newFilterPanel: {
      rule: {
        [idx1]: {
          $splice: [[idx2, 1]],
        },
      },
    },
  });
  if (newState.newFilterPanel.rule[idx1].length === 0) {
    return update(newState, {
      newFilterPanel: {
        rule: {
          $splice: [[idx1, 1]],
        },
      },
    });
  }
  return newState;
};

reducerMap[types.UPDATE_ACTIVE_TAB] = (state, action) =>
  update(state, {
    activeTabKey: { $set: action.key },
  });

reducerMap[types.UPDATE_META] = (state, action) =>
  update(state, {
    $merge: {
      categories: action.categories,
      dimensions: action.dimensions,
    },
  });

reducerMap[types.DONE_GETTING_FILTERS] = (state, action) =>
  update(state, {
    $merge: {
      filters: action.filters,
      dataCachedAt: moment().format(),
    },
  });

reducerMap[types.CLEAR_FILTER_CACHE] = (state) =>
  update(state, {
    $merge: {
      // refresh in the background (current behaviour)
      // filters: [],
      dataCachedAt: null,
    },
  });

reducerMap[types.UPDATE_SELECTED_FILTERS] = (state, action) => {
  const { filters } = action;
  return update(state, {
    savedFilterPanel: {
      selectedFilters: { $set: filters },
    },
  });
};

reducerMap[types.UPDATE_FILTER_CONDITIONS] = (state, action) =>
  update(state, {
    newFilterPanel: { $merge: action.payload },
  });

reducerMap[types.CHANGE_FILTER_CONTAINER_VISIBILITY] = (state, action) => {
  const { visible } = action;
  if (visible === false) {
    return update(state, {
      $merge: {
        visible: false,
        newFilterPanel: { ...initialState.newFilterPanel },
      },
    });
  }
  return update(state, {
    visible: { $set: true },
  });
};

reducerMap[commonTypes.APPLY_FILTER] = (state) =>
  update(state, {
    savedFilterPanel: {
      selectedFilters: { $set: [] },
    },
  });

reducerMap[types.SET_DEFAULT_FILTERS] = (state, action) =>
  update(state, {
    defaultFilters: {
      $set: action.filters,
    },
  });

const filterContainer = (state = initialState, action) =>
  reducerMap[action.type] ? reducerMap[action.type](state, action) : state;

export default filterContainer;
